/**
 * ============================================================================
 * SignSnapshotDialog — Phase 6b frontend (Snapshot Signing Tray)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: confirmation dialog for the QA Director / Site Quality Head signing
 *          a monthly quality-metric snapshot. Surfaces the content_hash the
 *          user is acknowledging, the period summary, an attestation textarea,
 *          and a WebAuthn 2FA challenge.
 *
 * REGULATORY:
 *   Snapshots are immutable once signed (audit-svc append-only store), so the
 *   2FA factor must be phishing-resistant — same gate as the disposition
 *   tray. If WebAuthn is unavailable we surface the enrollment path instead
 *   of letting the user submit something the server will reject.
 * ============================================================================ */

import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import {
  performAssertionCeremony,
  WebAuthnEnrollmentRequiredError,
  type AssertionOptionsResponse,
} from '../../auth/webauthn';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './SnapshotSigningTray.module.css';
import { useSignSnapshotMutation } from './useSignSnapshotMutation';
import type { QualitySnapshot } from './useSnapshotsQuery';

export interface SignSnapshotDialogProps {
  snapshot: QualitySnapshot;
  onClose: () => void;
  onSigned: () => void;
}

/**
 * Render a value cell for the metrics_summary table. Strings, numbers, and
 * booleans render verbatim; nested objects are JSON-stringified so the user
 * still sees what they're signing. Nothing here is parsed — the canonical
 * payload lives server-side.
 */
function renderSummaryValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function SignSnapshotDialog({
  snapshot,
  onClose,
  onSigned,
}: SignSnapshotDialogProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const signM = useSignSnapshotMutation();
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, onClose);

  const webauthnSupported = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential !== 'undefined',
    [],
  );

  const defaultAttestation = useMemo(
    () =>
      t('qualityMetrics.snapshots.attestation.default', {
        defaultValue:
          'I attest that the quality metrics for the period {{start}} – {{end}} ({{scope}}) accurately reflect site quality at the close of the period, and that no material adjustment is outstanding.',
        start: snapshot.period_start,
        end: snapshot.period_end,
        scope: snapshot.scope,
      }),
    [t, snapshot.period_start, snapshot.period_end, snapshot.scope],
  );

  const [attestationText, setAttestationText] = useState(defaultAttestation);
  const [hashConfirmed, setHashConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summaryEntries = useMemo(
    () => Object.entries(snapshot.metrics_summary ?? {}),
    [snapshot.metrics_summary],
  );

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }),
    [i18n.language],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!webauthnSupported) {
      setError(t('qualityMetrics.snapshots.errors.webauthnRequired', {
        defaultValue:
          'Phishing-resistant 2FA (WebAuthn) is required to sign quality-metric snapshots.',
      }));
      return;
    }
    if (!hashConfirmed) {
      setError(t('qualityMetrics.snapshots.errors.hashNotConfirmed', {
        defaultValue: 'You must confirm the content hash before signing.',
      }));
      return;
    }
    if (attestationText.trim().length < 10) {
      setError(t('qualityMetrics.snapshots.errors.attestationTooShort', {
        defaultValue: 'Attestation must be at least 10 characters.',
      }));
      return;
    }
    try {
      // C1 wire-up (2026-05-28): replace the fabricated `mock-webauthn-…`
      // string with a real assertion ceremony. The encoded assertion goes
      // into the sign request body; the server verifies it against the
      // user's registered credential before recording the signature.
      const assertion = await performAssertionCeremony(() =>
        apiFetch<AssertionOptionsResponse>(
          '/api/v1/auth/2fa/webauthn/assertion/options',
          { method: 'POST', body: {} },
        ),
      );
      await signM.mutateAsync({
        snapshotId: snapshot.snapshot_id,
        contentHash: snapshot.content_hash,
        attestationText: attestationText.trim(),
        webauthnAssertion: assertion,
      });
      onSigned();
    } catch (e: unknown) {
      if (e instanceof WebAuthnEnrollmentRequiredError) {
        setError(t('qualityMetrics.snapshots.errors.enrolmentRequired', {
          defaultValue:
            'No WebAuthn credential is registered for your account. Visit /2fa/enrollment and add a security key or platform authenticator before signing snapshots.',
        }));
        return;
      }
      setError(
        e instanceof Error
          ? e.message
          : t('qualityMetrics.snapshots.errors.serverFailed', {
              defaultValue: 'Server rejected the signature.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`${dialogStyles.dialog} ${styles.signDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('qualityMetrics.snapshots.dialog.title', {
              defaultValue: 'Sign quality-metric snapshot',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('qualityMetrics.snapshots.dialog.subtitle', {
              defaultValue:
                '{{scope}} · {{start}} – {{end}}. Once signed, the snapshot is immutable in the audit trail.',
              scope: snapshot.scope,
              start: dateFmt.format(new Date(snapshot.period_start)),
              end: dateFmt.format(new Date(snapshot.period_end)),
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {!webauthnSupported && (
            <div role="alert" className={styles.webauthnEnvelope}>
              <h3 className={styles.webauthnTitle}>
                {t('qualityMetrics.snapshots.dialog.webauthnHeading', {
                  defaultValue: 'Phishing-resistant 2FA required',
                })}
              </h3>
              <p>
                {t('qualityMetrics.snapshots.dialog.webauthnBody', {
                  defaultValue:
                    'This action requires a WebAuthn security key or platform authenticator. Your browser does not advertise WebAuthn support.',
                })}
              </p>
              <p>
                <a
                  href="/2fa/enrollment"
                  className={styles.webauthnLink}
                  aria-label={t('qualityMetrics.snapshots.dialog.webauthnSetupAria', {
                    defaultValue: 'Open the two-factor authentication setup page',
                  })}
                >
                  {t('qualityMetrics.snapshots.dialog.webauthnSetupLink', {
                    defaultValue: 'Set up 2FA →',
                  })}
                </a>
              </p>
            </div>
          )}

          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <section
            className={styles.hashSection}
            aria-labelledby={`${titleId}-hash`}
          >
            <h3 id={`${titleId}-hash`} className={styles.sectionHeading}>
              {t('qualityMetrics.snapshots.dialog.hashHeading', {
                defaultValue: 'Content hash',
              })}
            </h3>
            <code className={styles.hashValue}>{snapshot.content_hash}</code>
            <label className={styles.hashConfirmRow}>
              <input
                type="checkbox"
                checked={hashConfirmed}
                onChange={(e) => setHashConfirmed(e.target.checked)}
              />
              <span>
                {t('qualityMetrics.snapshots.dialog.hashConfirmLabel', {
                  defaultValue:
                    'I have reviewed the snapshot contents and confirm this hash matches my expectation.',
                })}
              </span>
            </label>
          </section>

          {summaryEntries.length > 0 && (
            <section
              className={styles.summarySection}
              aria-labelledby={`${titleId}-summary`}
            >
              <h3 id={`${titleId}-summary`} className={styles.sectionHeading}>
                {t('qualityMetrics.snapshots.dialog.summaryHeading', {
                  defaultValue: 'Snapshot summary',
                })}
              </h3>
              <dl className={styles.summaryGrid}>
                {summaryEntries.map(([k, v]) => (
                  <div key={k} className={styles.summaryRow}>
                    <dt className={styles.summaryKey}>{k}</dt>
                    <dd className={styles.summaryValue}>{renderSummaryValue(v)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="ss-attestation" className={dialogStyles.label}>
              {t('qualityMetrics.snapshots.dialog.attestationLabel', {
                defaultValue: 'Attestation text',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="ss-attestation"
              className={dialogStyles.input}
              rows={4}
              value={attestationText}
              onChange={(e) => setAttestationText(e.target.value)}
              required
            />
          </div>

          {webauthnSupported && (
            <p className={styles.webauthnHint} role="note">
              {t('qualityMetrics.snapshots.dialog.webauthnHint', {
                defaultValue:
                  'Submitting will prompt your security key or platform authenticator (Touch ID / Windows Hello).',
              })}
            </p>
          )}

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={signM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={signM.isPending}
              disabled={!webauthnSupported}
            >
              {t('qualityMetrics.snapshots.dialog.signSubmit', {
                defaultValue: 'Sign with WebAuthn',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
